"use client";

/**
 * useRedeemableQuery - TanStack Query hook for redeemable positions
 *
 * Centralizes /api/portfolio/redeemable behind a single cache key.
 * Replaces 5+ independent fetches with shared cache + 60s polling.
 */

import { useEffect } from "react";
import { useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";

const QUERY_KEY = ["portfolio", "redeemable"] as const;

/**
 * Once /api/dflow/positions/redeemable answers with 451 (geo-blocked —
 * US + restricted jurisdictions per Kalshi/DFlow compliance), there's
 * no point polling that endpoint every 60 s for the rest of the
 * session. Each poll costs a CF round-trip + a backend stack hop, and
 * the answer can't change without the user moving country. Flag it on
 * the first 451 and skip the call from then on. PM redeemables keep
 * polling normally.
 */
let dflowGeoBlocked = false;

/**
 * Grace window (ms) after a successful claim during which invalidation events
 * are ignored. Polymarket's Data API takes ~10-30s to index the redemption
 * transaction; refetching earlier repopulates the cache with already-claimed
 * positions and makes them reappear in the UI.
 */
const POST_REDEEM_GRACE_MS = 30_000;
let suppressInvalidationUntil = 0;

export interface RedeemablePosition {
  conditionId: string;
  title: string;
  outcome: string;
  shares: number;
  payout: number;
  /** Same number as payout — kept under the backend's name so the
   *  RedeemModal's `p.redeemableValue ?? p.payout` lookup hits a real
   *  field instead of falling back to 0. */
  redeemableValue?: number;
  tokenId: string;
  /** "polymarket" for PM positions (CLOB redeem) or "dflow" for Kalshi
   *  positions (DFlow redeem). Defaults to "polymarket" for back-compat
   *  with rows that don't carry the field yet. Match the literal the
   *  RedeemModal already uses internally. */
  source?: "polymarket" | "dflow";
  /** DFlow-only: position id used by /api/dflow/redeem. */
  positionId?: string;
}

export interface RedeemableData {
  count: number;
  totalRedeemable: number;
  positions: RedeemablePosition[];
}

/**
 * Apply an optimistic update to the shared redeemable cache after a successful
 * claim. Removes the claimed positions and opens a grace window during which
 * invalidation events (local or WebSocket-driven) are ignored, so the badges
 * don't flicker back while Polymarket's indexer catches up.
 *
 * Pass `"all"` to clear the whole cache (used by "claim all" flows that don't
 * track individual IDs). Otherwise pass the set of conditionIds that succeeded.
 */
export function applyRedeemOptimisticUpdate(
  queryClient: QueryClient,
  claimed: "all" | Set<string>,
): void {
  queryClient.setQueryData<RedeemableData>(QUERY_KEY, (old) => {
    if (!old) return old;
    if (claimed === "all") {
      return { count: 0, totalRedeemable: 0, positions: [] };
    }
    const remaining = old.positions.filter((p) => !claimed.has(p.conditionId));
    return {
      count: remaining.length,
      totalRedeemable: remaining.reduce((s, p) => s + (p.payout || 0), 0),
      positions: remaining,
    };
  });
  suppressInvalidationUntil = Date.now() + POST_REDEEM_GRACE_MS;
}

export function useRedeemableQuery(enabled = true) {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();

  // Invalidate on trade/position events — but skip during the post-redeem
  // grace window so optimistic updates aren't overwritten by a stale refetch.
  useEffect(() => {
    const handler = () => {
      if (Date.now() < suppressInvalidationUntil) return;
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    };
    window.addEventListener("copytrade:refresh", handler);
    window.addEventListener("positions:refresh", handler);
    window.addEventListener("portfolio:refresh", handler);
    return () => {
      window.removeEventListener("copytrade:refresh", handler);
      window.removeEventListener("positions:refresh", handler);
      window.removeEventListener("portfolio:refresh", handler);
    };
  }, [queryClient]);

  return useQuery<RedeemableData>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      // Fetch both PM and DFlow redeemables in parallel. DFlow may 503
      // when the user doesn't have a Kalshi account yet — treat that as
      // "no kalshi positions" instead of failing the whole query. 451
      // means the user is in a geo-blocked jurisdiction (Kalshi/DFlow
      // compliance) so we permanently disable the dflow leg for the
      // rest of the session (see `dflowGeoBlocked` flag above).
      const [pmRes, kRes] = await Promise.allSettled([
        authFetch("/api/portfolio/redeemable").then((r) => r.ok ? r.json() : null),
        dflowGeoBlocked
          ? Promise.resolve(null)
          : authFetch("/api/dflow/positions/redeemable").then((r) => {
              if (r.status === 451) {
                dflowGeoBlocked = true;
                return null;
              }
              return r.ok ? r.json() : null;
            }),
      ]);

      const pm = pmRes.status === "fulfilled" ? pmRes.value : null;
      const k = kRes.status === "fulfilled" ? kRes.value : null;

      const pmPositions: RedeemablePosition[] = (pm?.positions || []).map((p: any) => ({
        conditionId: p.conditionId,
        // Backend transformToRedeemable returns `marketQuestion`; older
        // shape used `title`. Accept either so the row label doesn't
        // collapse to the generic "Polymarket position" placeholder.
        title: p.marketQuestion || p.title || "",
        outcome: p.outcome,
        shares: Number(p.shares ?? 0),
        // Backend returns `redeemableValue`; the modal also reads
        // `payout` for legacy callers — fill both so the "YOU WON $X.XX"
        // headline + per-row +$X.XX both render the real number.
        payout: Number(p.redeemableValue ?? p.payout ?? 0),
        redeemableValue: Number(p.redeemableValue ?? p.payout ?? 0),
        tokenId: p.tokenId,
        source: "polymarket" as const,
      }));

      // /api/dflow/positions/redeemable returns rows with shape:
      //   { id, kalshiTicker, marketTitle, outcome, size, redeemableValue, ... }
      // Normalize them to the same RedeemablePosition shape so the modal
      // can render both sources from one list. conditionId falls back to
      // kalshiTicker (it's the natural id for DFlow positions).
      const kalshiPositions: RedeemablePosition[] = (k?.data || []).map((p: any) => ({
        conditionId: String(p.kalshiTicker || p.id || ""),
        title: String(p.marketTitle || p.kalshiTicker || ""),
        outcome: String(p.outcome || ""),
        shares: Number(p.size ?? p.shares ?? 0),
        payout: Number(p.redeemableValue ?? 0),
        tokenId: String(p.id || p.kalshiTicker || ""),
        source: "dflow" as const,
        positionId: String(p.id || ""),
      }));

      const positions = [...pmPositions, ...kalshiPositions];
      const count = positions.length;
      const totalRedeemable =
        Number(pm?.totalRedeemable ?? 0) + Number(k?.totalRedeemable ?? 0);

      return { count, totalRedeemable, positions };
    },
    enabled: isLoggedIn && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
